
import { useEffect, useMemo, useState } from "react";
import { WizardWorkspace, type WizardTab } from "../blueprints/WizardWorkspace";
import {
  AlertCircle,
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Building2,
  Filter,
  Globe2,
  Info,
  Layers3,
  LockKeyhole,
  Package,
  Plus,
  Settings2,
  Shield,
  ShoppingBag,
  Store,
  Tag,
  Trash2,
  Users2,
  X,
} from "lucide-react";
import { cn } from "../../lib/utils";
import type {
  CatalogPublicationEditorProps as LegacyCatalogPublicationEditorProps,
  CatalogPublicationScope,
  PublicationAccessEditorProps as LegacyPublicationAccessEditorProps,
  PublicationAccessSetItem,
} from "../../contracts/commerce";
import type {
  CommerceHelpCalloutViewModel,
  CommerceInlineFieldHelp,
  CommerceMetadataEntryViewModel,
  CommerceMetadataValueType,
  CommerceSelectOptionViewModel,
  PublicationAccessEditorViewModel,
  PublicationAudiencePreviewViewModel,
  PublicationEditorFormValue,
  PublicationSectionFormValue,
  PublicationEditorViewModel,
  PublicationProductSelectionValue,
  PublicationSelectorMode,
  PublicationSelectorRuleViewModel,
} from "../../contracts/commerce-admin";
import { Input } from "../../internal/primitives/input";
import { Label } from "../../internal/primitives/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../internal/primitives/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../internal/primitives/select";
import {
  SectionHeading,
  Field,
  Checkbox,
  inputStyles,
  Surface,
  VerticalStack,
  DescriptionText,
} from "../blocks";
import { CommerceObjectSelector } from "./CommerceObjectSelector";

type CatalogPublicationEditorViewModelProps = {
  viewModel: PublicationEditorViewModel;
  accessEditorViewModel?: PublicationAccessEditorViewModel;
  error?: string | null;
  className?: string;
}

type CatalogPublicationEditorLegacyProps = LegacyCatalogPublicationEditorProps & {
  accessProps?: LegacyPublicationAccessEditorProps;
}

type CatalogPublicationEditorSheetProps =
  | CatalogPublicationEditorViewModelProps
  | CatalogPublicationEditorLegacyProps;

type PublicationAccessEditorViewModelProps = {
  viewModel: PublicationAccessEditorViewModel;
  className?: string;
}

type PublicationAccessEditorProps =
  | PublicationAccessEditorViewModelProps
  | LegacyPublicationAccessEditorProps;

const EMPTY_SELECT_OPTIONS: CommerceSelectOptionViewModel[] = [];
const EMPTY_SELECTED_IDS: string[] = [];

type EditorSurfaceModel = {
  isOpen: boolean;
  isSaving: boolean;
  title: string;
  subtitle: string;
  publicationId?: string | null;
  saveActionLabel: string;
  value: PublicationEditorFormValue;
  catalogOptions: CommerceSelectOptionViewModel[];
  pricebookOptions: CommerceSelectOptionViewModel[];
  commissionPlanOptions: CommerceSelectOptionViewModel[];
  scopeOptions: CommerceSelectOptionViewModel[];
  statusOptions: CommerceSelectOptionViewModel[];
  selectorModeOptions: CommerceSelectOptionViewModel[];
  productOptions: CommerceSelectOptionViewModel[];
  variantOptions: CommerceSelectOptionViewModel[];
  selectorAttributeOptions: CommerceSelectOptionViewModel[];
  selectorOperatorOptions: CommerceSelectOptionViewModel[];
  audiencePreview: PublicationAudiencePreviewViewModel;
  fieldHelp: CommerceInlineFieldHelp[];
  callouts: CommerceHelpCalloutViewModel[];
  validationErrors: Record<string, string>;
  error?: string | null;
  onChange: (patch: Partial<PublicationEditorFormValue>) => void;
  onChangeProductSelection: (value: PublicationProductSelectionValue) => void;
  onSave: () => Promise<void>;
  onClose: () => void;
}

type PublicationScopeKind =
  | "public"
  | "owner_only"
  | "organizations"
  | "tenants"
  | "stores"
  | "agents"
  | "unknown";

const defaultAudiencePreview: PublicationAudiencePreviewViewModel = {
  reachSummary: "Publication reach updates as scope and access rules change.",
}

const legacyScopeOptions: CommerceSelectOptionViewModel[] = [
  {
    value: "public",
    label: "Public",
    description: "Any eligible storefront session can browse and buy from the publication.",
  },
  {
    value: "owner_only",
    label: "Owner only",
    description: "Reserved for the catalog owner tenant.",
  },
  {
    value: "shared_organizations",
    label: "Organizations",
    description: "Grant reach to approved reseller organizations.",
  },
  {
    value: "shared_tenants",
    label: "Tenants",
    description: "Grant reach directly to named selling tenants.",
  },
  {
    value: "shared_stores",
    label: "Stores",
    description: "Grant reach only to selected storefront or branch operators.",
  },
  {
    value: "agent_only",
    label: "Agents",
    description: "Grant reach only to selected selling agents.",
  },
];

const legacyStatusOptions: CommerceSelectOptionViewModel[] = [
  { value: "draft", label: "Draft" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
];

const legacySelectorModeOptions: CommerceSelectOptionViewModel[] = [
  { value: "all_products", label: "All products", description: "Expose the full catalog." },
  {
    value: "explicit_products",
    label: "Explicit products",
    description: "Choose which products belong in the publication.",
  },
  {
    value: "explicit_variants",
    label: "Explicit variants",
    description: "Choose exactly which variants are available.",
  },
  {
    value: "rule_based",
    label: "Rule based",
    description: "Use semantic rules to shape publication inventory.",
  },
];

const legacySelectorAttributeOptions: CommerceSelectOptionViewModel[] = [
  { value: "product_type", label: "Product type" },
  { value: "service_class", label: "Service class" },
  { value: "product_group", label: "Product group" },
  { value: "availability", label: "Availability tag" },
];

const legacySelectorOperatorOptions: CommerceSelectOptionViewModel[] = [
  { value: "equals", label: "Equals" },
  { value: "contains", label: "Contains" },
  { value: "in", label: "In list" },
];

const metadataValueTypeOptions: CommerceSelectOptionViewModel[] = [
  { value: "string", label: "String" },
  { value: "number", label: "Number" },
  { value: "boolean", label: "Boolean" },
  { value: "json", label: "JSON" },
];

function isViewModelProps(
  props: CatalogPublicationEditorSheetProps
): props is CatalogPublicationEditorViewModelProps {
  return "viewModel" in props;
}

function isAccessViewModelProps(
  props: PublicationAccessEditorProps
): props is PublicationAccessEditorViewModelProps {
  return "viewModel" in props;
}

function normalizeScopeKind(value: string | undefined | null): PublicationScopeKind {
  const normalized = value?.trim().toLowerCase();

  if (!normalized) {
    return "unknown";
  }
  if (normalized === "public") {
    return "public";
  }
  if (normalized === "owner_only" || normalized.includes("owner")) {
    return "owner_only";
  }
  if (normalized.includes("organization")) {
    return "organizations";
  }
  if (normalized.includes("tenant")) {
    return "tenants";
  }
  if (normalized.includes("store")) {
    return "stores";
  }
  if (normalized.includes("agent")) {
    return "agents";
  }

  return "unknown";
}

function getScopePresentation(kind: PublicationScopeKind) {
  switch (kind) {
    case "public":
      return {
        label: "Public",
        icon: Globe2,
        description: "Anyone arriving through the storefront can browse and buy eligible offers.",
        consequence: "Use when the publication should behave like a public storefront assortment.",
      };
    case "owner_only":
      return {
        label: "Owner only",
        icon: LockKeyhole,
        description: "Only the catalog owner tenant can browse or sell from this publication.",
        consequence: "Use for first-party selling, staged launch work, or internal validation.",
      };
    case "organizations":
      return {
        label: "Organization scoped",
        icon: Building2,
        description: "Approved reseller organizations gain reach through their active member tenants.",
        consequence: "Use when access is negotiated at the organization level.",
      };
    case "tenants":
      return {
        label: "Tenant scoped",
        icon: Shield,
        description: "Only named selling tenants can expose and transact against the catalog.",
        consequence: "Use for private reseller relationships and direct commercial agreements.",
      };
    case "stores":
      return {
        label: "Store scoped",
        icon: Store,
        description: "Only approved store or branch locations can sell from the publication.",
        consequence: "Use when selling rights differ by physical or operational location.",
      };
    case "agents":
      return {
        label: "Agent only",
        icon: Users2,
        description: "Only explicitly approved agents may browse and sell from the publication.",
        consequence: "Use when access must follow named sellers instead of the whole tenant.",
      };
    default:
      return {
        label: "Scoped publication",
        icon: Shield,
        description: "The publication is constrained by operator-defined reach rules.",
        consequence: "Review the resolved access rules before publishing.",
      };
  }
}

function getSelectionModePresentation(mode: PublicationSelectorMode) {
  switch (mode) {
    case "all_products":
      return {
        icon: Layers3,
        label: "All products",
        description: "Expose the entire catalog unless pricing or access rules say otherwise.",
      };
    case "explicit_products":
      return {
        icon: Package,
        label: "Explicit products",
        description: "Choose which products belong in scope. Their eligible variants remain available.",
      };
    case "explicit_variants":
      return {
        icon: ShoppingBag,
        label: "Explicit variants",
        description: "Choose the exact sellable variants operators may offer.",
      };
    case "rule_based":
      return {
        icon: Filter,
        label: "Rule based",
        description: "Use semantic rules to resolve publication inventory by attribute.",
      };
    default:
      return {
        icon: Layers3,
        label: "Selection",
        description: "Choose how publication inventory is resolved.",
      };
  }
}

function getToneClasses(tone: CommerceHelpCalloutViewModel["tone"]) {
  switch (tone) {
    case "danger":
      return "border-destructive/20 bg-destructive/5 text-destructive";
    case "warning":
      return "border-warning/30 bg-warning/10 text-warning-foreground";
    case "success":
      return "border-primary/20 bg-primary/5 text-foreground";
    case "info":
    default:
      return "border-primary/20 bg-primary/5 text-foreground";
  }
}

function parseAccessIdentifiers(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[\s,]+/)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  );
}

function getOptionLabel(
  options: CommerceSelectOptionViewModel[],
  value: string | null | undefined,
  fallback = "Not set"
) {
  if (!value) {
    return fallback;
  }
  return options.find((option) => option.value === value)?.label ?? value;
}

function findHelp(
  fieldHelp: CommerceInlineFieldHelp[],
  field: string
): CommerceInlineFieldHelp | undefined {
  return fieldHelp.find((item) => item.field === field);
}

function inferMetadataValueType(value: unknown): CommerceMetadataValueType {
  if (typeof value === "number") {
    return "number";
  }
  if (typeof value === "boolean") {
    return "boolean";
  }
  if (typeof value === "object" && value !== null) {
    return "json";
  }
  return "string";
}

function stringifyMetadataValue(value: unknown, valueType: CommerceMetadataValueType): string {
  if (valueType === "json") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  if (valueType === "boolean") {
    return value ? "true" : "false";
  }
  return value == null ? "" : String(value);
}

function metadataRecordToEntries(
  metadata?: Record<string, unknown>
): CommerceMetadataEntryViewModel[] {
  if (!metadata) {
    return [];
  }

  return Object.entries(metadata).map(([key, value], index) => {
    const valueType = inferMetadataValueType(value);
    return {
      id: `legacy-metadata-${index}`,
      key,
      value: stringifyMetadataValue(value, valueType),
      valueType,
    };
  });
}

function metadataEntriesToRecord(entries: CommerceMetadataEntryViewModel[]) {
  const record: Record<string, unknown> = {};

  entries.forEach((entry) => {
    if (!entry.key.trim()) {
      return;
    }

    if (entry.valueType === "number") {
      const parsed = Number(entry.value);
      record[entry.key] = Number.isNaN(parsed) ? entry.value : parsed;
      return;
    }

    if (entry.valueType === "boolean") {
      record[entry.key] = entry.value === "true";
      return;
    }

    if (entry.valueType === "json") {
      try {
        record[entry.key] = JSON.parse(entry.value);
      } catch {
        record[entry.key] = entry.value;
      }
      return;
    }

    record[entry.key] = entry.value;
  });

  return record;
}

function normalizeLegacyProductSelection(
  selector?: Record<string, unknown>
): PublicationProductSelectionValue {
  const includedProductIds = Array.isArray(selector?.includedProductIds)
    ? selector.includedProductIds.filter((value): value is string => typeof value === "string")
    : Array.isArray(selector?.included_product_ids)
      ? selector.included_product_ids.filter((value): value is string => typeof value === "string")
    : [];
  const includedVariantIds = Array.isArray(selector?.includedVariantIds)
    ? selector.includedVariantIds.filter((value): value is string => typeof value === "string")
    : Array.isArray(selector?.included_variant_ids)
      ? selector.included_variant_ids.filter((value): value is string => typeof value === "string")
    : [];
  const rules = Array.isArray(selector?.rules)
    ? selector.rules
        .filter((rule): rule is Record<string, unknown> => typeof rule === "object" && rule !== null)
        .map((rule, index) => ({
          ruleId: String(rule.ruleId ?? `legacy-rule-${index}`),
          attribute: String(rule.attribute ?? ""),
          selectorKey: String(rule.selectorKey ?? rule.selector_key ?? ""),
          operator: String(rule.operator ?? ""),
          valueLabels: Array.isArray(rule.valueLabels)
            ? rule.valueLabels.filter((value): value is string => typeof value === "string")
            : Array.isArray(rule.values)
              ? rule.values.filter((value): value is string => typeof value === "string")
            : [],
          effectSummary: String(rule.effectSummary ?? ""),
        }))
    : [];

  const mode = typeof selector?.mode === "string" ? selector.mode : "";
  const normalizedMode: PublicationSelectorMode =
    mode === "explicit_products" ||
    mode === "explicit_variants" ||
    mode === "rule_based" ||
    mode === "all_products"
      ? mode
      : rules.length > 0
        ? "rule_based"
        : includedVariantIds.length > 0
          ? "explicit_variants"
          : includedProductIds.length > 0
            ? "explicit_products"
            : "all_products";

  return {
    mode: normalizedMode,
    includedProductIds,
    includedVariantIds,
    rules,
  };
}

function buildRuleEffectSummary(
  rule: Pick<PublicationSelectorRuleViewModel, "attribute" | "selectorKey" | "operator" | "valueLabels">,
  attributeOptions: CommerceSelectOptionViewModel[],
  operatorOptions: CommerceSelectOptionViewModel[]
) {
  const attributeLabel = getOptionLabel(attributeOptions, rule.attribute, "attribute");
  const operatorLabel = getOptionLabel(operatorOptions, rule.operator, "operator");
  const scopedAttribute = rule.selectorKey?.trim() ? `${attributeLabel} (${rule.selectorKey.trim()})` : attributeLabel;
  const valueSummary = rule.valueLabels.length > 0 ? rule.valueLabels.join(", ") : "value";
  return `${scopedAttribute} ${operatorLabel} ${valueSummary}`;
}

function createMetadataEntry(): CommerceMetadataEntryViewModel {
  return {
    id: `metadata-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    key: "",
    value: "",
    valueType: "string",
  };
}

function createRule(
  attributeOptions: CommerceSelectOptionViewModel[],
  operatorOptions: CommerceSelectOptionViewModel[]
): PublicationSelectorRuleViewModel {
  const attribute = attributeOptions[0]?.value ?? "";
  const operator = operatorOptions[0]?.value ?? "";

  return {
    ruleId: `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    attribute,
    selectorKey: "",
    operator,
    valueLabels: [],
    effectSummary: buildRuleEffectSummary(
      { attribute, selectorKey: "", operator, valueLabels: [] },
      attributeOptions,
      operatorOptions
    ),
  };
}

function createSection(): PublicationSectionFormValue {
  return {
    sectionKey: "",
    name: "",
    description: "",
    status: "active",
    displayMode: "list",
    selectionMode: "browse",
    sortOrder: 0,
    productSelection: {
      mode: "all_products",
      includedProductIds: [],
      includedVariantIds: [],
      rules: [],
    },
    suggestedPlacements: [],
  };
}

function SuggestedPlacementsEditor({
  placements = [],
  productOptions,
  variantOptions,
  onChange,
}: {
  placements: PublicationSectionFormValue["suggestedPlacements"];
  productOptions: CommerceSelectOptionViewModel[];
  variantOptions: CommerceSelectOptionViewModel[];
  onChange: (placements: PublicationSectionFormValue["suggestedPlacements"]) => void;
}) {
  const rows: Array<PublicationSectionFormValue["suggestedPlacements"][number] | null> = [
    ...placements,
    ...(placements.length < 3 ? [null] : []),
  ];
  const selectedKeys = new Set(placements.map((placement) => `${placement.targetKind}:${placement.targetId}`));

  const updatePlacement = (index: number, value: string) => {
    if (value === "unselected") {
      onChange(placements.filter((_, currentIndex) => currentIndex !== index));
      return;
    }
    const separator = value.indexOf(":");
    const targetKind = value.slice(0, separator) as "product" | "variant";
    const targetId = value.slice(separator + 1);
    const next = [...placements];
    next[index] = { targetKind, targetId };
    onChange(next.slice(0, 3));
  };

  const movePlacement = (index: number, direction: -1 | 1) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= placements.length) return;
    const next = [...placements];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    onChange(next);
  };

  return (
    <div className="mt-5 border-t border-border/50 pt-5">
      <div className="mb-3">
        <p className="text-xs font-black text-foreground">Suggested choices</p>
        <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
          Choose and order up to three products or variants shown before the storefront expands the full section.
        </p>
      </div>
      <div className="space-y-2">
        {rows.map((placement, index) => {
          const value = placement ? `${placement.targetKind}:${placement.targetId}` : "unselected";
          return (
            <div key={`${value}-${index}`} className="flex items-center gap-2">
              <span className="w-6 text-center text-[10px] font-black text-muted-foreground">{index + 1}</span>
              <Select value={value} onValueChange={(next) => updatePlacement(index, next)}>
                <SelectTrigger className="h-9 flex-1 text-xs" aria-label={`Suggested choice ${index + 1}`}>
                  <SelectValue placeholder="Add a suggested choice" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unselected">{placement ? "Remove choice" : "Add a suggested choice"}</SelectItem>
                  {productOptions.map((option) => {
                    const key = `product:${option.value}`;
                    return (
                      <SelectItem key={key} value={key} disabled={selectedKeys.has(key) && key !== value}>
                        Product · {option.label}
                      </SelectItem>
                    );
                  })}
                  {variantOptions.map((option) => {
                    const key = `variant:${option.value}`;
                    return (
                      <SelectItem key={key} value={key} disabled={selectedKeys.has(key) && key !== value}>
                        Variant · {option.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {placement && (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label={`Move suggested choice ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => movePlacement(index, -1)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-control border border-border/70 text-muted-foreground disabled:opacity-30"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move suggested choice ${index + 1} down`}
                    disabled={index === placements.length - 1}
                    onClick={() => movePlacement(index, 1)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-control border border-border/70 text-muted-foreground disabled:opacity-30"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function updateValueForType(value: string, nextType: CommerceMetadataValueType) {
  if (nextType === "number") {
    return value && !Number.isNaN(Number(value)) ? value : "";
  }
  if (nextType === "json") {
    return value.trim() ? value : "{}";
  }
  if (nextType === "boolean") {
    return value === "true" ? "true" : "false";
  }
  return value;
}

function mapLegacyOptions(
  options: Array<{ value: string; label: string }>
): CommerceSelectOptionViewModel[] {
  return options.map((option) => ({
    value: option.value,
    label: option.label,
  }));
}

function renderAudienceFacts(audiencePreview: PublicationAudiencePreviewViewModel) {
  const facts = [
    audiencePreview.organizationCountLabel,
    audiencePreview.tenantCountLabel,
    audiencePreview.storeCountLabel,
    audiencePreview.agentCountLabel,
  ].filter((value): value is string => Boolean(value));

  if (facts.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-2">
      {facts.map((fact) => (
        <span
          key={fact}
          className="rounded-control bg-muted/30 px-2.5 py-1 text-[11px] font-semibold text-foreground"
        >
          {fact}
        </span>
      ))}
    </div>
  );
}

function HelpPopover({
  title,
  body,
  variant = "icon",
  align = "start",
}: {
  title: string;
  body: string;
  variant?: "icon" | "link";
  align?: "start" | "center" | "end";
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={title}
          className={cn(
            "inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground",
            variant === "icon"
              ? "rounded-control p-1"
              : "text-[11px] font-semibold"
          )}
        >
          <Info className="h-3.5 w-3.5" />
          {variant === "link" && <span>{title}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-72 space-y-2 p-3">
        <p className="text-xs font-black tracking-tight text-foreground">{title}</p>
        <p className="text-xs leading-5 text-muted-foreground">{body}</p>
      </PopoverContent>
    </Popover>
  );
}



function FieldError({ message }: { message?: string }) {
  if (!message) {
    return null;
  }

  return <p className="text-xs font-semibold text-destructive">{message}</p>;
}

function PublicationSelectionPicker({
  title,
  body,
  options,
  selectedIds,
  searchValue,
  onSearchChange,
  onSelectionChange,
}: {
  title: string;
  body: string;
  options: CommerceSelectOptionViewModel[];
  selectedIds: string[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onSelectionChange: (ids: string[]) => void;
}) {
  const filteredOptions = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) {
      return options;
    }

    return options.filter((option) => {
      const haystack = [
        option.label,
        option.description ?? "",
        option.helperText ?? "",
        ...(option.keywords ?? []),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [options, searchValue]);

  const selectedOptions = selectedIds
    .map((id) => options.find((option) => option.value === id))
    .filter(Boolean) as CommerceSelectOptionViewModel[];

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2">
        <p className="text-sm font-bold text-foreground">{title}</p>
        <HelpPopover title={title} body={body} />
      </div>

      <div className="rounded-control bg-muted/20 p-3">
        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground">
          Selected
        </p>
        {selectedOptions.length > 0 ? (
          <div className="mt-3 flex flex-wrap gap-2">
            {selectedOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => onSelectionChange(selectedIds.filter((id) => id !== option.value))}
                className="inline-flex items-center gap-2 rounded-control border border-border/70 bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary"
              >
                <span className="truncate">{option.label}</span>
                <X className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">Nothing selected yet.</p>
        )}
      </div>

      <div className="space-y-2">
        <Label className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground">
          Search catalog inventory
        </Label>
        <Input
          type="search"
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder="Filter by title, group, or helper text"
          className="h-10 text-sm"
        />
      </div>

      <div className="max-h-64 space-y-2 overflow-auto pr-1">
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const isSelected = selectedIds.includes(option.value);

            return (
              <button
                key={option.value}
                type="button"
                onClick={() =>
                  onSelectionChange(
                    isSelected
                      ? selectedIds.filter((id) => id !== option.value)
                      : [...selectedIds, option.value]
                  )
                }
                className={cn(
                  "flex w-full items-start justify-between rounded-control border px-3 py-3 text-left transition-colors",
                  isSelected
                    ? "border-primary/40 bg-primary/5"
                    : "border-border/70 bg-background hover:border-primary/30 hover:bg-muted/30"
                )}
              >
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{option.label}</p>
                  {(option.description || option.helperText) && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {option.description ?? option.helperText}
                    </p>
                  )}
                </div>
                <span
                  className={cn(
                    "ml-3 rounded-control border px-2 py-1 text-[10px] font-black uppercase tracking-[0.18em]",
                    isSelected
                      ? "border-primary/30 bg-primary text-primary-foreground"
                      : "border-border/70 bg-muted/30 text-muted-foreground"
                  )}
                >
                  {isSelected ? "Selected" : "Add"}
                </span>
              </button>
            );
          })
        ) : (
          <div className="rounded-control border border-dashed border-border/70 bg-muted/20 px-4 py-6 text-center text-xs text-muted-foreground">
            No matches for the current filter.
          </div>
        )}
      </div>
    </div>
  );
}

function useLegacyEditorModel(
  props: CatalogPublicationEditorLegacyProps | null
): EditorSurfaceModel {
  const [value, setValue] = useState<PublicationEditorFormValue>({
    storefrontKey: "",
    catalogId: "",
    scope: "owner_only",
    status: "draft",
    defaultPricebookId: "",
    commissionPlanId: "",
    productSelection: {
      mode: "all_products",
      includedProductIds: [],
      includedVariantIds: [],
      rules: [],
    },
    sections: [],
    metadata: [],
  });

  useEffect(() => {
    if (!props) {
      return;
    }

    // Legacy editor mode mirrors incoming record props into a local draft form state.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setValue({
      storefrontKey: props.initialData?.storefrontKey ?? "",
      catalogId: props.initialData?.catalogKey ?? "",
      scope: props.initialData?.scope ?? "owner_only",
      status: props.initialData?.status ?? "draft",
      defaultPricebookId: props.initialData?.defaultPricebookId ?? "",
      commissionPlanId: "",
      productSelection: normalizeLegacyProductSelection(props.initialData?.productSelector),
      sections: [],
      metadata: metadataRecordToEntries(props.initialData?.metadata),
    });
  }, [props]);

  if (!props) {
    return {
      isOpen: false,
      isSaving: false,
      title: "",
      subtitle: "",
      publicationId: null,
      saveActionLabel: "Save publication",
      value,
      catalogOptions: [],
      pricebookOptions: [],
      commissionPlanOptions: [],
      scopeOptions: legacyScopeOptions,
      statusOptions: legacyStatusOptions,
      selectorModeOptions: legacySelectorModeOptions,
      productOptions: [],
      variantOptions: [],
      selectorAttributeOptions: legacySelectorAttributeOptions,
      selectorOperatorOptions: legacySelectorOperatorOptions,
      audiencePreview: defaultAudiencePreview,
      fieldHelp: [],
      callouts: [],
      validationErrors: {},
      onChange: () => undefined,
      onChangeProductSelection: () => undefined,
      onSave: async () => undefined,
      onClose: () => undefined,
    };
  }

  return {
    isOpen: props.isOpen,
    isSaving: Boolean(props.isSaving),
    title: props.title,
    subtitle: props.publicationId
      ? `Adjust publication reach and defaults for ${props.publicationId}.`
      : "Create a publication that controls catalog reach, pricing defaults, and sell-side scope.",
    publicationId: props.publicationId,
    saveActionLabel: "Save publication",
    value,
    catalogOptions: [],
    pricebookOptions: mapLegacyOptions(props.pricebookOptions),
    commissionPlanOptions: [],
    scopeOptions: legacyScopeOptions,
    statusOptions: legacyStatusOptions,
    selectorModeOptions: legacySelectorModeOptions,
    productOptions: [],
    variantOptions: [],
    selectorAttributeOptions: legacySelectorAttributeOptions,
    selectorOperatorOptions: legacySelectorOperatorOptions,
    audiencePreview: defaultAudiencePreview,
    fieldHelp: [],
    callouts: [],
    validationErrors: {},
    error: props.error,
    onChange: (patch) => setValue((current) => ({ ...current, ...patch })),
    onChangeProductSelection: (productSelection) =>
      setValue((current) => ({ ...current, productSelection })),
    onSave: async () => {
      props.onSave({
        storefrontKey: value.storefrontKey,
        catalogKey: value.catalogId,
        scope: value.scope as CatalogPublicationScope,
        status: value.status,
        defaultPricebookId: value.defaultPricebookId ?? "",
        commissionPlanId: value.commissionPlanId ?? "",
        productSelection: value.productSelection,
        sections: value.sections,
        productSelector: {
          mode: value.productSelection.mode,
          includedProductIds: value.productSelection.includedProductIds,
          includedVariantIds: value.productSelection.includedVariantIds,
          rules: value.productSelection.rules,
        },
        metadata: metadataEntriesToRecord(value.metadata),
      });
    },
    onClose: props.onClose,
  };
}

export function PublicationAccessEditor(props: PublicationAccessEditorProps) {
  const isViewModel = isAccessViewModelProps(props);
  const [searchValue, setSearchValue] = useState("");
  const [bulkPasteValue, setBulkPasteValue] = useState("");

  const scopeKind = normalizeScopeKind(isViewModel ? props.viewModel.kind : props.scope);
  const scopePresentation = getScopePresentation(scopeKind);
  const ScopeIcon = scopePresentation.icon;
  const viewModelOptions = isViewModel ? props.viewModel.options : EMPTY_SELECT_OPTIONS;
  const selectedIds = isViewModel ? props.viewModel.selectedIds : EMPTY_SELECTED_IDS;

  useEffect(() => {
    if (!isViewModel) {
      return;
    }
    const normalizedCurrent = parseAccessIdentifiers(bulkPasteValue);
    if (
      normalizedCurrent.length === selectedIds.length &&
      normalizedCurrent.every((value, index) => value === selectedIds[index])
    ) {
      return;
    }

    // Keep the bulk-paste textarea synchronized with the externally managed selection list.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setBulkPasteValue(selectedIds.join("\n"));
  }, [bulkPasteValue, isViewModel, selectedIds]);

  const filteredOptions = useMemo(() => {
    if (!isViewModel) {
      return [];
    }

    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) {
      return viewModelOptions;
    }

    return viewModelOptions.filter((option) => {
      const haystack = [
        option.label,
        option.description ?? "",
        option.helperText ?? "",
        ...(option.keywords ?? []),
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearch);
    });
  }, [isViewModel, searchValue, viewModelOptions]);

  if (scopeKind === "public" || scopeKind === "owner_only") {
    return (
      <div className={cn("rounded-control bg-muted/20 px-4 py-4", props.className)}>
        <div className="flex items-start gap-3">
          <div className="rounded-control bg-background p-2 text-foreground">
            <ScopeIcon className="h-4 w-4" />
          </div>
          <div className="space-y-1">
            <h3 className="text-sm font-black tracking-tight text-foreground">
              {scopePresentation.label} publications do not use allow-lists
            </h3>
            <p className="text-sm text-muted-foreground">{scopePresentation.description}</p>
            <p className="text-xs text-muted-foreground">{scopePresentation.consequence}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isViewModel) {
    const sections: Array<{
      key: "organizations" | "tenants" | "stores" | "agents";
      label: string;
      items: PublicationAccessSetItem[];
    }> = [
      { key: "organizations", label: "Organizations", items: props.organizations },
      { key: "tenants", label: "Tenants", items: props.tenants },
      { key: "stores", label: "Stores", items: props.stores },
      { key: "agents", label: "Agents", items: props.agents },
    ];

    return (
      <div className={cn("space-y-4", props.className)}>
        <div className="rounded-control bg-primary/5 px-4 py-4">
          <p className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground">
            Access guidance
          </p>
          <p className="mt-2 text-sm font-semibold text-foreground">{scopePresentation.description}</p>
          <p className="mt-1 text-xs text-muted-foreground">{scopePresentation.consequence}</p>
        </div>

        {sections.map((section) => (
          <div key={section.key} className="border-t border-border/60 pt-4 first:border-t-0 first:pt-0">
            <p className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground">
              {section.label}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {section.items.length > 0
                ? `${section.items.length} selected`
                : `No ${section.label.toLowerCase()} selected yet.`}
            </p>

            {section.items.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-2">
                {section.items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() =>
                      props.onUpdateSet(
                        section.key,
                        section.items.filter((candidate) => candidate.id !== item.id).map((candidate) => candidate.id)
                      )
                    }
                    className="inline-flex items-center gap-2 rounded-control border border-border/70 bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-primary/30 hover:text-primary"
                  >
                    <span>{item.label}</span>
                    <X className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-control border border-dashed border-border/70 bg-muted/20 px-4 py-5 text-xs text-muted-foreground">
                Add selections from the surrounding access-management workflow.
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn("space-y-4", props.className)}>
      <div className="rounded-control bg-muted/20 px-4 py-4">
        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground">
          {props.viewModel.title}
        </p>
        <h3 className="mt-2 text-base font-black tracking-tight text-foreground">
          {props.viewModel.selectionSummary}
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">{props.viewModel.description}</p>
        <p className="mt-2 text-xs text-muted-foreground">{props.viewModel.helpText}</p>
        {props.viewModel.bulkPasteLabel && (
          <div className="mt-3 inline-flex rounded-control bg-background px-2.5 py-1 text-[11px] font-semibold text-foreground">
            {props.viewModel.bulkPasteLabel}
          </div>
        )}
      </div>

      {props.viewModel.allowBulkPaste ? (
        <div className="border-t border-border/60 pt-4">
          <Label className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground">
            {props.viewModel.bulkPasteLabel ?? "Seller IDs"}
          </Label>
          <textarea
            value={bulkPasteValue}
            onChange={(event) => {
              const nextValue = event.target.value;
              setBulkPasteValue(nextValue);
              props.viewModel.onSelectionChange(parseAccessIdentifiers(nextValue));
            }}
            rows={4}
            placeholder="Paste one ID per line or use commas."
            className={cn(inputStyles, "mt-3 min-h-28 resize-y font-mono text-xs font-medium")}
          />
          <p className="mt-2 text-xs text-muted-foreground">
            Saving replaces the full allow-list for this publication scope.
          </p>
        </div>
      ) : null}

      {props.viewModel.callouts.map((callout) => (
        <div
          key={callout.id}
          className={cn("rounded-panel border px-4 py-4 text-sm", getToneClasses(callout.tone))}
        >
          <div className="flex items-start gap-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-semibold">{callout.title}</p>
              <p className="mt-1 text-xs opacity-90">{callout.body}</p>
            </div>
          </div>
        </div>
      ))}

      <div className="border-t border-border/60 pt-4">
        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground">
          Selected sellers
        </p>
        {props.viewModel.selectedOptions.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {props.viewModel.selectedOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() =>
                  props.viewModel.onSelectionChange(
                    props.viewModel.selectedIds.filter((id) => id !== option.value)
                  )
                }
                className="inline-flex items-center gap-2 rounded-control border border-border/70 bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition-colors hover:border-primary/30 hover:text-primary"
              >
                <span>{option.label}</span>
                <X className="h-3.5 w-3.5" />
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-4 rounded-control border border-dashed border-border/70 bg-muted/20 px-4 py-5 text-xs text-muted-foreground">
            No sellers selected yet.
          </div>
        )}
      </div>

      {props.viewModel.searchable && (
        <div className="border-t border-border/60 pt-4">
          <Label className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground">
            Search available sellers
          </Label>
          <Input
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Filter by label, description, or keyword"
            className="mt-3 h-10 text-sm"
          />

          <div className="mt-4 max-h-72 space-y-2 overflow-auto pr-1">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                const isSelected = props.viewModel.selectedIds.includes(option.value);

                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() =>
                      props.viewModel.onSelectionChange(
                        isSelected
                          ? props.viewModel.selectedIds.filter((id) => id !== option.value)
                          : [...props.viewModel.selectedIds, option.value]
                      )
                    }
                    className={cn(
                      "flex w-full items-start justify-between rounded-control border px-3 py-3 text-left transition-colors",
                      isSelected
                        ? "border-primary/40 bg-primary/5"
                        : "border-border/70 bg-background hover:border-primary/30 hover:bg-muted/30"
                    )}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground">{option.label}</p>
                      {(option.description || option.helperText) && (
                        <p className="mt-1 text-xs text-muted-foreground">
                          {option.description ?? option.helperText}
                        </p>
                      )}
                    </div>
                    <span
                      className={cn(
                        "ml-3 rounded-control border px-2 py-1 text-[10px] font-black uppercase tracking-[0.18em]",
                        isSelected
                          ? "border-primary/30 bg-primary text-primary-foreground"
                          : "border-border/70 bg-muted/30 text-muted-foreground"
                      )}
                    >
                      {isSelected ? "Selected" : "Add"}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="rounded-control border border-dashed border-border/70 bg-muted/20 px-4 py-5 text-xs text-muted-foreground">
                {props.viewModel.emptySearchMessage ?? "No sellers match the current filter."}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}



export function CatalogPublicationEditorWizard(
  props: CatalogPublicationEditorSheetProps
) {
  const [activeTabId, setActiveTabId] = useState<string>("identity");
  const [productSearch, setProductSearch] = useState("");
  const [variantSearch, setVariantSearch] = useState("");
  const [selectedSectionIndex, setSelectedSectionIndex] = useState<number | null>(null);

  const legacyModel = useLegacyEditorModel(isViewModelProps(props) ? null : props);
  const model: EditorSurfaceModel = isViewModelProps(props)
    ? {
        isOpen: props.viewModel.isOpen,
        isSaving: props.viewModel.state === "saving",
        title: props.viewModel.title,
        subtitle: props.viewModel.subtitle,
        publicationId: props.viewModel.publicationId,
        saveActionLabel: props.viewModel.saveActionLabel,
        value: props.viewModel.value,
        catalogOptions: props.viewModel.catalogOptions,
        pricebookOptions: props.viewModel.pricebookOptions,
        commissionPlanOptions: props.viewModel.commissionPlanOptions,
        scopeOptions: props.viewModel.scopeOptions,
        statusOptions: props.viewModel.statusOptions,
        selectorModeOptions: props.viewModel.selectorModeOptions,
        productOptions: props.viewModel.productOptions,
        variantOptions: props.viewModel.variantOptions,
        selectorAttributeOptions: props.viewModel.selectorAttributeOptions,
        selectorOperatorOptions: props.viewModel.selectorOperatorOptions,
        audiencePreview: props.viewModel.audiencePreview,
        fieldHelp: props.viewModel.fieldHelp,
        callouts: props.viewModel.callouts,
        validationErrors: props.viewModel.validationErrors,
        error: props.error,
        onChange: props.viewModel.onChange,
        onChangeProductSelection: props.viewModel.onChangeProductSelection,
        onSave: props.viewModel.onSave,
        onClose: props.viewModel.onClose,
      }
    : legacyModel;

  const accessAvailable = isViewModelProps(props)
    ? Boolean(props.accessEditorViewModel)
    : Boolean(props.accessProps);
  const accessCanSave = isViewModelProps(props) ? Boolean(props.accessEditorViewModel) : false;

  const resolvedActiveTabId =
    activeTabId === "access" && !accessAvailable ? "identity" : activeTabId;

  if (!model.isOpen) {
    return null;
  }

  const scopePresentation = getScopePresentation(normalizeScopeKind(model.value.scope));
  const ScopeIcon = scopePresentation.icon;
  const selectionPresentation = getSelectionModePresentation(model.value.productSelection.mode);
  const SelectionIcon = selectionPresentation.icon;
  const updateMetadataEntry = (
    entryId: string,
    patch: Partial<CommerceMetadataEntryViewModel>
  ) => {
    model.onChange({
      metadata: model.value.metadata.map((entry) =>
        entry.id === entryId ? { ...entry, ...patch } : entry
      ),
    });
  };

  const removeMetadataEntry = (entryId: string) => {
    model.onChange({
      metadata: model.value.metadata.filter((entry) => entry.id !== entryId),
    });
  };

  const addMetadataEntry = () => {
    model.onChange({
      metadata: [...model.value.metadata, createMetadataEntry()],
    });
  };

  const updateRule = (
    ruleId: string,
    patch: Partial<PublicationSelectorRuleViewModel>
  ) => {
    model.onChangeProductSelection({
      ...model.value.productSelection,
      rules: model.value.productSelection.rules.map((rule) => {
        if (rule.ruleId !== ruleId) {
          return rule;
        }

        const nextRule = { ...rule, ...patch };
        return {
          ...nextRule,
          effectSummary: buildRuleEffectSummary(
            nextRule,
            model.selectorAttributeOptions,
            model.selectorOperatorOptions
          ),
        };
      }),
    });
  };

  const removeRule = (ruleId: string) => {
    model.onChangeProductSelection({
      ...model.value.productSelection,
      rules: model.value.productSelection.rules.filter((rule) => rule.ruleId !== ruleId),
    });
  };

  const addRule = () => {
    model.onChangeProductSelection({
      ...model.value.productSelection,
      rules: [
        ...model.value.productSelection.rules,
        createRule(model.selectorAttributeOptions, model.selectorOperatorOptions),
      ],
    });
  };

  const updateSection = (index: number, patch: Partial<PublicationSectionFormValue>) => {
    model.onChange({
      sections: model.value.sections.map((section, currentIndex) =>
        currentIndex === index ? { ...section, ...patch } : section
      ),
    });
  };

  const removeSection = (index: number) => {
    model.onChange({
      sections: model.value.sections.filter((_, currentIndex) => currentIndex !== index),
    });
  };

  const addSection = () => {
    model.onChange({
      sections: [...model.value.sections, createSection()],
    });
  };

  const updateSectionRule = (
    sectionIndex: number,
    ruleId: string,
    patch: Partial<PublicationSelectorRuleViewModel>
  ) => {
    const section = model.value.sections[sectionIndex];
    if (!section) {
      return;
    }
    updateSection(sectionIndex, {
      productSelection: {
        ...section.productSelection,
        rules: section.productSelection.rules.map((rule) => {
          if (rule.ruleId !== ruleId) {
            return rule;
          }
          const nextRule = { ...rule, ...patch };
          return {
            ...nextRule,
            effectSummary: buildRuleEffectSummary(
              nextRule,
              model.selectorAttributeOptions,
              model.selectorOperatorOptions
            ),
          };
        }),
      },
    });
  };

  const addSectionRule = (sectionIndex: number) => {
    const section = model.value.sections[sectionIndex];
    if (!section) {
      return;
    }
    updateSection(sectionIndex, {
      productSelection: {
        ...section.productSelection,
        rules: [
          ...section.productSelection.rules,
          createRule(model.selectorAttributeOptions, model.selectorOperatorOptions),
        ],
      },
    });
  };

  const removeSectionRule = (sectionIndex: number, ruleId: string) => {
    const section = model.value.sections[sectionIndex];
    if (!section) {
      return;
    }
    updateSection(sectionIndex, {
      productSelection: {
        ...section.productSelection,
        rules: section.productSelection.rules.filter((rule) => rule.ruleId !== ruleId),
      },
    });
  };

  const handlePrimaryAction = async () => {
    if (resolvedActiveTabId === "access" && isViewModelProps(props) && props.accessEditorViewModel) {
      await props.accessEditorViewModel.onSave();
      return;
    }
    await model.onSave();
  };

  const tabs: WizardTab[] = [
    { id: "identity", label: "Publication Identity", icon: <Settings2 className="h-4 w-4" /> },
    { id: "scope", label: "Visibility Scope", icon: <Globe2 className="h-4 w-4" /> },
    { id: "commercial", label: "Commercial Defaults", icon: <Tag className="h-4 w-4" /> },
    { id: "selection", label: "Product Selection", icon: <Package className="h-4 w-4" /> },
    { id: "sections", label: "Storefront Sections", icon: <Layers3 className="h-4 w-4" />, count: model.value.sections.length || undefined },
    { id: "metadata", label: "Advanced Metadata", icon: <ShoppingBag className="h-4 w-4" />, count: model.value.metadata.length || undefined },
  ];

  if (accessAvailable) {
    tabs.push({ id: "access", label: "Access Reach", icon: <Shield className="h-4 w-4" /> });
  }

  const renderTabContent = () => {
    if (resolvedActiveTabId === "access" && accessAvailable) {
      return (
        <div className="space-y-6">
          <div className="mb-6">
            <h2 className="text-2xl font-black tracking-tight">Access Reach</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Manage explicitly approved sellers for this scoped publication.
            </p>
          </div>
          {isViewModelProps(props) && props.accessEditorViewModel ? (
            <PublicationAccessEditor viewModel={props.accessEditorViewModel} />
          ) : !isViewModelProps(props) && props.accessProps ? (
            <PublicationAccessEditor {...props.accessProps} />
          ) : null}
        </div>
      );
    }

    switch (resolvedActiveTabId) {
      case "identity":
        return (
          <VerticalStack gap={8}>
            <section>
              <SectionHeading
                title="Publication Identity"
                subtitle="Anchor the publication to a storefront and catalog so operators know exactly what surface and assortment this publication controls."
              />
              <Surface className="mt-4 border-border/60 p-4 sm:p-6">
                <div className="grid gap-5 lg:grid-cols-2">
                  <Field
                    label="Storefront key"
                    error={model.validationErrors.storefrontKey}
                    description={findHelp(model.fieldHelp, "storefrontKey")?.body}
                  >
                    <Input
                      id="publication-storefront"
                      value={model.value.storefrontKey}
                      onChange={(event) => model.onChange({ storefrontKey: event.target.value })}
                      placeholder="main-retail"
                      className="h-10 text-sm"
                    />
                  </Field>

                  <Field
                    label={model.catalogOptions.length > 0 ? "Catalog" : "Catalog key"}
                    error={model.validationErrors.catalogId}
                    description={findHelp(model.fieldHelp, "catalogId")?.body}
                  >
                    {model.catalogOptions.length > 0 ? (
                      <Select value={model.value.catalogId} onValueChange={(catalogId) => model.onChange({
                        catalogId,
                        defaultPricebookId: "",
                        productSelection: { mode: "all_products", includedProductIds: [], includedVariantIds: [], rules: [] },
                        sections: [],
                      })}>
                        <SelectTrigger id="publication-catalog" className="h-10 text-sm">
                          <SelectValue placeholder="Choose a catalog" />
                        </SelectTrigger>
                        <SelectContent>
                          {model.catalogOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id="publication-catalog"
                        value={model.value.catalogId}
                        onChange={(event) => model.onChange({ catalogId: event.target.value })}
                        placeholder="standard-2026"
                        className="h-10 text-sm"
                      />
                    )}
                  </Field>

                  <Field
                    label="Lifecycle"
                    error={model.validationErrors.status}
                    description={findHelp(model.fieldHelp, "status")?.body}
                  >
                    <Select value={model.value.status} onValueChange={(status) => model.onChange({ status })}>
                      <SelectTrigger className="h-10 text-sm">
                        <SelectValue placeholder="Choose a status" />
                      </SelectTrigger>
                      <SelectContent>
                        {model.statusOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </Surface>
            </section>
          </VerticalStack>
        );

      case "scope":
        return (
          <VerticalStack gap={8}>
            <section>
              <SectionHeading
                title="Visibility Scope"
                subtitle="Decide who can see and resell the publication. Scope changes alter downstream browse reach, reseller eligibility, and which access rules matter."
              />
              <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {model.scopeOptions.map((option) => {
                  const optionScope = getScopePresentation(normalizeScopeKind(option.value));
                  const Icon = optionScope.icon;
                  const isSelected = model.value.scope === option.value;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => model.onChange({ scope: option.value })}
                      className={cn(
                        "rounded-md border flex flex-col text-left transition-all p-3 sm:p-5 h-full",
                        isSelected
                          ? "border-primary/50 bg-primary/5 shadow-sm"
                          : "border-border/40 bg-muted/5 hover:border-primary/30 hover:bg-muted/10"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <div className={cn("rounded-lg p-2", isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                          <Icon className="h-5 w-5" />
                        </div>
                        {isSelected && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-primary">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-black text-foreground">{option.label}</p>
                      <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
                        {option.description ?? optionScope.description}
                      </p>
                    </button>
                    );
                    })}
                    </div>
                    </section>
            <section>
              <SectionHeading title="Audience Context" subtitle="Review how the current scope affects resolved reach." />
              <Surface className="mt-4 border-border/60 bg-muted/10 p-4 sm:p-5">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="rounded-lg bg-background p-2 text-foreground shadow-sm border border-border/40 sm:p-2.5">
                    <ScopeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <div className="space-y-1.5 min-w-0 flex-1">
                    <p className="text-sm font-black text-foreground">{scopePresentation.label}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{scopePresentation.description}</p>
                    <p className="text-xs font-semibold text-foreground mt-2">
                      {model.audiencePreview.reachSummary}
                    </p>
                    {renderAudienceFacts(model.audiencePreview)}
                  </div>
                </div>
              </Surface>
              <div className="mt-4">
                <Field label="Configuration Note" description={findHelp(model.fieldHelp, "scope")?.body}>
                  <FieldError message={model.validationErrors.scope} />
                </Field>
              </div>
            </section>
          </VerticalStack>
        );

      case "commercial":
        return (
          <VerticalStack gap={8}>
            <section>
              <SectionHeading
                title="Commercial Defaults"
                subtitle="Set the pricing and commission terms operators inherit by default. These defaults travel with browse, quote, and order flows unless a more specific assignment overrides them."
              />
              
              <div className="mt-4 grid gap-3 sm:mt-6 sm:gap-4 lg:grid-cols-2">
                <Surface className="border-border/60 bg-muted/10 p-3 sm:p-5">
                  <div className="space-y-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Default Pricebook</p>
                    <p className="text-sm font-black text-foreground">
                      {getOptionLabel(model.pricebookOptions, model.value.defaultPricebookId, "No default pricebook")}
                    </p>
                    <p className="mt-1.5 hidden text-xs leading-relaxed text-muted-foreground sm:block">
                      Used when no more specific pricebook assignment matches the selling context.
                    </p>
                  </div>
                </Surface>
                <Surface className="border-border/60 bg-muted/10 p-3 sm:p-5">
                  <div className="space-y-1">
                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Commission Plan</p>
                    <p className="text-sm font-black text-foreground">
                      {getOptionLabel(model.commissionPlanOptions, model.value.commissionPlanId, "No commission plan")}
                    </p>
                    <p className="mt-1.5 hidden text-xs leading-relaxed text-muted-foreground sm:block">
                      Defines the payout model snapshot attached to downstream orders.
                    </p>
                  </div>
                </Surface>
              </div>

              <Surface className="mt-5 border-border/60 p-4 sm:mt-8 sm:p-6">
                <div className="grid gap-5 lg:grid-cols-2">
                  <Field
                    label="Default pricebook"
                    error={model.validationErrors.defaultPricebookId}
                    description={findHelp(model.fieldHelp, "defaultPricebookId")?.body}
                  >
                    <Select
                      value={model.value.defaultPricebookId || "none"}
                      onValueChange={(defaultPricebookId) =>
                        model.onChange({ defaultPricebookId: defaultPricebookId === "none" ? "" : defaultPricebookId })
                      }
                    >
                      <SelectTrigger className="h-10 text-sm">
                        <SelectValue placeholder="Choose a pricebook" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No default pricebook</SelectItem>
                        {model.pricebookOptions
                          .filter(option => !option.metadata?.catalogId || option.metadata.catalogId === model.value.catalogId)
                          .map((option) => (
                          <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field
                    label="Commission plan"
                    error={model.validationErrors.commissionPlanId}
                    description={findHelp(model.fieldHelp, "commissionPlanId")?.body}
                  >
                    <Select
                      value={model.value.commissionPlanId || "none"}
                      onValueChange={(commissionPlanId) =>
                        model.onChange({ commissionPlanId: commissionPlanId === "none" ? "" : commissionPlanId })
                      }
                    >
                      <SelectTrigger className="h-10 text-sm">
                        <SelectValue placeholder="Choose a commission plan" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No commission plan</SelectItem>
                        {model.commissionPlanOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              </Surface>
            </section>
          </VerticalStack>
        );

      case "selection":
        return (
          <VerticalStack gap={10}>
            <section>
              <SectionHeading
                title="Product Selection"
              />
              <p className="mt-2 text-sm text-muted-foreground">
                This product selection applies everywhere this publication is used. For different market options, create another publication and select it in Storefront bindings.
              </p>
              
              <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {model.selectorModeOptions.map((option) => {
                  const mode = option.value as PublicationSelectorMode;
                  const presentation = getSelectionModePresentation(mode);
                  const Icon = presentation.icon;
                  const isSelected = model.value.productSelection.mode === mode;

                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => model.onChangeProductSelection({ ...model.value.productSelection, mode })}
                      className={cn(
                        "rounded-md border flex flex-col text-left transition-all p-3 sm:p-4 h-full",
                        isSelected
                          ? "border-primary/50 bg-primary/5 shadow-sm"
                          : "border-border/40 bg-muted/5 hover:border-primary/30 hover:bg-muted/10"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className={cn("rounded-lg p-2", isSelected ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground")}>
                          <Icon className="h-5 w-5" />
                        </div>
                        {isSelected && (
                          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-primary">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-xs font-black text-foreground">{option.label}</p>
                      <p className="mt-1 text-[10px] text-muted-foreground leading-relaxed line-clamp-2">
                        {option.description ?? presentation.description}
                      </p>
                    </button>
                    );
                    })}
                    </div>
                    </section>
            <section>
              <Surface className="border-border/60 bg-muted/10 p-3 sm:p-5">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="rounded-lg border border-border/40 bg-background p-2 text-foreground shadow-sm sm:p-2.5">
                    <SelectionIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-black text-foreground">{selectionPresentation.label}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{selectionPresentation.description}</p>
                  </div>
                </div>
              </Surface>
            </section>

            {model.value.productSelection.mode === "all_products" && (
              <section>
                <Surface className="border-border/40 border-dashed bg-muted/5 p-8 text-center">
                  <DescriptionText className="max-w-md mx-auto">
                    Every eligible catalog product follows this publication. Choose explicit picks or rules when the assortment should be curated.
                  </DescriptionText>
                </Surface>
              </section>
            )}

            {model.value.productSelection.mode === "explicit_products" && (
              <section>
                <PublicationSelectionPicker
                  title="Explicit product picks"
                  body="Choose the products that should be visible under this publication."
                  options={model.productOptions}
                  selectedIds={model.value.productSelection.includedProductIds}
                  searchValue={productSearch}
                  onSearchChange={setProductSearch}
                  onSelectionChange={(includedProductIds) =>
                    model.onChangeProductSelection({ ...model.value.productSelection, includedProductIds })
                  }
                />
              </section>
            )}

            {model.value.productSelection.mode === "explicit_variants" && (
              <section>
                <PublicationSelectionPicker
                  title="Explicit variant picks"
                  body="Choose the exact sellable variants that should be exposed."
                  options={model.variantOptions}
                  selectedIds={model.value.productSelection.includedVariantIds}
                  searchValue={variantSearch}
                  onSearchChange={setVariantSearch}
                  onSelectionChange={(includedVariantIds) =>
                    model.onChangeProductSelection({ ...model.value.productSelection, includedVariantIds })
                  }
                />
              </section>
            )}

            {model.value.productSelection.mode === "rule_based" && (
              <section>
                <div className="flex items-center justify-between gap-4 mb-4">
                  <SectionHeading
                    title="Selector Rules"
                    subtitle="Compose semantic rules instead of editing raw selector blobs."
                  />
                  <button
                    type="button"
                    onClick={addRule}
                    className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-xs font-black text-primary-foreground transition-all hover:opacity-90 shadow-sm"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add rule
                  </button>
                </div>

                {model.value.productSelection.rules.length > 0 ? (
                  <VerticalStack gap={4}>
                    {model.value.productSelection.rules.map((rule) => (
                      <Surface key={rule.ruleId} className="border-border/60 p-4 sm:p-6">
                        <div className="grid gap-4 sm:gap-6 xl:grid-cols-[1fr_1fr_1fr_1.3fr_auto]">
                          <Field label="Attribute">
                            <Select value={rule.attribute} onValueChange={(attribute) => updateRule(rule.ruleId, { attribute })}>
                              <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Choose attribute" /></SelectTrigger>
                              <SelectContent>
                                {model.selectorAttributeOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </Field>
                          <Field label="Selector key">
                            <Input
                              value={rule.selectorKey || ""}
                              onChange={(event) => updateRule(rule.ruleId, { selectorKey: event.target.value })}
                              placeholder="technology or signup_role"
                              className="h-10 text-sm"
                            />
                          </Field>
                          <Field label="Operator">
                            <Select value={rule.operator} onValueChange={(operator) => updateRule(rule.ruleId, { operator })}>
                              <SelectTrigger className="h-10 text-sm"><SelectValue placeholder="Choose operator" /></SelectTrigger>
                              <SelectContent>
                                {model.selectorOperatorOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </Field>
                          <Field label="Match values">
                            <Input
                              value={rule.valueLabels.join(", ")}
                              onChange={(event) =>
                                updateRule(rule.ruleId, {
                                  valueLabels: event.target.value.split(",").map((value) => value.trim()).filter(Boolean),
                                })
                              }
                              placeholder="fiber, residential, featured"
                              className="h-10 text-sm"
                            />
                          </Field>
                          <div className="flex items-end">
                            <button
                              type="button"
                              onClick={() => removeRule(rule.ruleId)}
                              aria-label="Remove selector rule"
                              className="inline-flex h-10 items-center justify-center rounded-md border border-border/40 bg-background px-4 text-muted-foreground transition-all hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                        <div className="mt-4 rounded-lg bg-muted/10 p-3 border border-border/40 sm:mt-6 sm:p-4">
                          <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">Effect summary</p>
                          <p className="mt-1 text-sm font-semibold text-foreground">{rule.effectSummary}</p>
                        </div>
                      </Surface>
                    ))}
                  </VerticalStack>
                ) : (
                  <Surface className="border-border/40 border-dashed bg-muted/5 p-12 text-center">
                    <DescriptionText>No selector rules defined yet.</DescriptionText>
                  </Surface>
                )}
              </section>
            )}

          </VerticalStack>
        );

      case "sections": {
        const selectedSection =
          selectedSectionIndex !== null
            ? (model.value.sections[selectedSectionIndex] ?? null)
            : null;

        return (
          <div className="flex h-full min-h-0">
            {/* LEFT: Section navigator */}
            <div
              className={cn(
                "flex flex-col overflow-hidden border-r border-border/40",
                "w-full lg:w-72 xl:w-[300px] lg:flex-shrink-0",
                selectedSection !== null ? "hidden lg:flex" : "flex",
              )}
            >
              {/* Navigator header */}
              <div className="shrink-0 flex items-center justify-between border-b border-border/40 bg-muted/5 px-5 py-4">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground/60">
                    Sections
                  </span>
                  <span className="rounded border border-border/40 bg-muted/60 px-1.5 py-0.5 text-[9px] font-black text-muted-foreground/80">
                    {model.value.sections.length}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    addSection();
                    setSelectedSectionIndex(model.value.sections.length);
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-background px-3 py-1.5 text-[10px] font-black uppercase tracking-tight text-foreground transition-all hover:bg-muted"
                >
                  <Plus className="h-3 w-3" />
                  Add
                </button>
              </div>

              {/* Section list */}
              <div className="flex-1 overflow-y-auto">
                {model.value.sections.length === 0 ? (
                  <div className="px-5 py-12 text-center">
                    <Layers3 className="mx-auto mb-3 h-8 w-8 text-muted-foreground/20" />
                    <p className="text-sm font-black text-foreground">No sections yet</p>
                    <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                      Add a section to organize storefront groups like Plans, Equipment, and Add-ons.
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-border/30">
                    {model.value.sections.map((section, idx) => (
                      <button
                        key={`section-nav-${idx}`}
                        type="button"
                        onClick={() => setSelectedSectionIndex(idx)}
                        className={cn(
                          "w-full flex items-start gap-3 border-l-2 px-5 py-4 text-left transition-all",
                          selectedSectionIndex === idx
                            ? "border-l-primary bg-primary/5"
                            : "border-l-transparent hover:bg-muted/20",
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-black text-foreground">
                            {section.name || `Section ${idx + 1}`}
                          </p>
                          <p className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground/70">
                            {section.sectionKey || "No key"}
                          </p>
                        </div>
                        <span
                          className={cn(
                            "shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider",
                            section.status === "active"
                              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                              : section.status === "hidden"
                                ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                : "border-border/40 bg-muted/60 text-muted-foreground",
                          )}
                        >
                          {section.status}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT: Section detail editor */}
            <div
              className={cn(
                "min-w-0 flex-1 overflow-y-auto",
                selectedSection !== null ? "block" : "hidden lg:block",
              )}
            >
              {selectedSection === null || selectedSectionIndex === null ? (
                <div className="flex h-full min-h-[400px] items-center justify-center px-8 py-16 text-center">
                  <div>
                    <Layers3 className="mx-auto mb-3 h-10 w-10 text-muted-foreground/20" />
                    <p className="text-sm font-black text-foreground">Select a section</p>
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      {model.value.sections.length > 0
                        ? "Choose a section from the list to configure it."
                        : "Add a section to get started."}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="px-5 py-5 sm:px-8 sm:py-6">
                  {/* Mobile back button */}
                  <button
                    type="button"
                    onClick={() => setSelectedSectionIndex(null)}
                    className="mb-5 flex items-center gap-2 text-xs font-bold text-muted-foreground transition-all hover:text-foreground lg:hidden"
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    All Sections
                  </button>

                  {/* Section header */}
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <h2 className="text-xl font-black tracking-tight text-foreground">
                        {selectedSection.name || `Section ${selectedSectionIndex + 1}`}
                      </h2>
                      <p className="mt-0.5 font-mono text-xs text-muted-foreground/70">
                        {selectedSection.sectionKey || "No key set"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const nextLength = model.value.sections.length - 1;
                        removeSection(selectedSectionIndex);
                        setSelectedSectionIndex(
                          nextLength === 0
                            ? null
                            : selectedSectionIndex >= nextLength
                              ? nextLength - 1
                              : selectedSectionIndex,
                        );
                      }}
                      aria-label={`Delete section ${selectedSection.name || selectedSectionIndex + 1}`}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-destructive/40 hover:bg-destructive/5 hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <VerticalStack gap={6}>
                    {/* Identity */}
                    <Surface className="border-border/60 p-5 sm:p-6">
                      <p className="mb-4 text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground/60">
                        Identity
                      </p>
                      <div className="grid gap-5 sm:grid-cols-2">
                        <Field label="Section Key">
                          <Input
                            value={selectedSection.sectionKey}
                            onChange={(e) =>
                              updateSection(selectedSectionIndex, { sectionKey: e.target.value })
                            }
                            className="h-10 font-mono text-sm"
                            placeholder="plans"
                          />
                        </Field>
                        <Field label="Name">
                          <Input
                            value={selectedSection.name}
                            onChange={(e) =>
                              updateSection(selectedSectionIndex, { name: e.target.value })
                            }
                            className="h-10 text-sm"
                            placeholder="Plans"
                          />
                        </Field>
                        <div className="sm:col-span-2">
                          <Field label="Description">
                            <Input
                              value={selectedSection.description || ""}
                              onChange={(e) =>
                                updateSection(selectedSectionIndex, {
                                  description: e.target.value,
                                })
                              }
                              className="h-10 text-sm"
                              placeholder="Choose the primary service for this address."
                            />
                          </Field>
                        </div>
                      </div>
                    </Surface>

                    {/* Configuration */}
                    <Surface className="border-border/60 p-5 sm:p-6">
                      <p className="mb-4 text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground/60">
                        Configuration
                      </p>
                      <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
                        <Field label="Status">
                          <Select
                            value={selectedSection.status}
                            onValueChange={(status) =>
                              updateSection(selectedSectionIndex, { status })
                            }
                          >
                            <SelectTrigger className="h-10 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="hidden">Hidden</SelectItem>
                              <SelectItem value="archived">Archived</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Display Mode">
                          <Select
                            value={selectedSection.displayMode}
                            onValueChange={(displayMode) =>
                              updateSection(selectedSectionIndex, { displayMode })
                            }
                          >
                            <SelectTrigger className="h-10 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="list">List</SelectItem>
                              <SelectItem value="cards">Cards</SelectItem>
                              <SelectItem value="grid">Grid</SelectItem>
                              <SelectItem value="featured">Featured</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Selection Mode">
                          <Select
                            value={selectedSection.selectionMode}
                            onValueChange={(selectionMode) =>
                              updateSection(selectedSectionIndex, { selectionMode })
                            }
                          >
                            <SelectTrigger className="h-10 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="browse">Browse</SelectItem>
                              <SelectItem value="single_required">Single Required</SelectItem>
                              <SelectItem value="single_optional">Single Optional</SelectItem>
                              <SelectItem value="multi_optional">Multi Optional</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Sort Order">
                          <Input
                            type="number"
                            value={String(selectedSection.sortOrder)}
                            onChange={(e) =>
                              updateSection(selectedSectionIndex, {
                                sortOrder: Number(e.target.value || 0),
                              })
                            }
                            className="h-10 text-sm"
                          />
                        </Field>
                      </div>
                    </Surface>

                    {/* Section Inventory */}
                    <div>
                      <div className="mb-5">
                        <p className="text-[10px] font-black uppercase tracking-[0.22em] text-muted-foreground/60">
                          Section Inventory
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                          Define which products or variants appear in this storefront group.
                        </p>
                      </div>

                      {/* Selection mode cards */}
                      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        {model.selectorModeOptions.map((option) => {
                          const sectionMode = option.value as PublicationSelectorMode;
                          const presentation = getSelectionModePresentation(sectionMode);
                          const ModeIcon = presentation.icon;
                          const isActive = selectedSection.productSelection.mode === sectionMode;

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() =>
                                updateSection(selectedSectionIndex, {
                                  productSelection: {
                                    ...selectedSection.productSelection,
                                    mode: sectionMode,
                                  },
                                })
                              }
                              className={cn(
                                "flex flex-col items-start rounded-md border p-3.5 text-left transition-all",
                                isActive
                                  ? "border-primary/50 bg-primary/5 shadow-sm"
                                  : "border-border/40 bg-muted/5 hover:border-primary/30 hover:bg-muted/10",
                              )}
                            >
                              <div
                                className={cn(
                                  "mb-2 rounded-lg p-1.5",
                                  isActive
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-muted text-muted-foreground",
                                )}
                              >
                                <ModeIcon className="h-3.5 w-3.5" />
                              </div>
                              <p className="text-[11px] font-black text-foreground">{option.label}</p>
                            </button>
                          );
                        })}
                      </div>

                      {selectedSection.productSelection.mode === "all_products" && (
                        <Surface className="border-border/40 border-dashed bg-muted/5 p-8 text-center">
                          <DescriptionText className="mx-auto max-w-sm">
                            This section exposes all products from the publication's resolved selection.
                          </DescriptionText>
                        </Surface>
                      )}

                      {selectedSection.productSelection.mode === "explicit_products" && (
                        <PublicationSelectionPicker
                          title="Section products"
                          body="Choose which products belong in this section."
                          options={model.productOptions}
                          selectedIds={selectedSection.productSelection.includedProductIds}
                          searchValue={productSearch}
                          onSearchChange={setProductSearch}
                          onSelectionChange={(includedProductIds) =>
                            updateSection(selectedSectionIndex, {
                              productSelection: {
                                ...selectedSection.productSelection,
                                includedProductIds,
                              },
                            })
                          }
                        />
                      )}

                      {selectedSection.productSelection.mode === "explicit_variants" && (
                        <PublicationSelectionPicker
                          title="Section variants"
                          body="Choose which variants belong in this section."
                          options={model.variantOptions}
                          selectedIds={selectedSection.productSelection.includedVariantIds}
                          searchValue={variantSearch}
                          onSearchChange={setVariantSearch}
                          onSelectionChange={(includedVariantIds) =>
                            updateSection(selectedSectionIndex, {
                              productSelection: {
                                ...selectedSection.productSelection,
                                includedVariantIds,
                              },
                            })
                          }
                        />
                      )}

                      {selectedSection.productSelection.mode === "rule_based" && (
                        <VerticalStack gap={4}>
                          <div className="flex items-center justify-between gap-4">
                            <p className="text-xs font-black uppercase tracking-widest text-muted-foreground">
                              Section Rules
                            </p>
                            <button
                              type="button"
                              onClick={() => addSectionRule(selectedSectionIndex)}
                              className="inline-flex h-8 items-center gap-2 rounded-md border border-border/40 bg-muted px-3 text-[10px] font-black uppercase tracking-wider text-foreground shadow-sm transition-all hover:bg-muted/80"
                            >
                              <Plus className="h-3 w-3" />
                              Add rule
                            </button>
                          </div>

                          {selectedSection.productSelection.rules.length === 0 ? (
                            <Surface className="border-border/40 border-dashed bg-muted/5 p-8 text-center">
                              <DescriptionText>
                                No section rules defined yet. Add a rule to filter inventory.
                              </DescriptionText>
                            </Surface>
                          ) : (
                            selectedSection.productSelection.rules.map((rule) => (
                              <Surface key={rule.ruleId} className="border-border/40 bg-background p-4">
                                <div className="grid gap-4 xl:grid-cols-[1fr_1fr_1fr_1.2fr_auto]">
                                  <Field label="Attribute">
                                    <Select
                                      value={rule.attribute}
                                      onValueChange={(attribute) =>
                                        updateSectionRule(selectedSectionIndex, rule.ruleId, {
                                          attribute,
                                        })
                                      }
                                    >
                                      <SelectTrigger className="h-9 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {model.selectorAttributeOptions.map((option) => (
                                          <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </Field>
                                  <Field label="Selector Key">
                                    <Input
                                      value={rule.selectorKey || ""}
                                      onChange={(e) =>
                                        updateSectionRule(selectedSectionIndex, rule.ruleId, {
                                          selectorKey: e.target.value,
                                        })
                                      }
                                      className="h-9 text-xs"
                                      placeholder="technology"
                                    />
                                  </Field>
                                  <Field label="Operator">
                                    <Select
                                      value={rule.operator}
                                      onValueChange={(operator) =>
                                        updateSectionRule(selectedSectionIndex, rule.ruleId, {
                                          operator,
                                        })
                                      }
                                    >
                                      <SelectTrigger className="h-9 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {model.selectorOperatorOptions.map((option) => (
                                          <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </Field>
                                  <Field label="Match Values">
                                    <Input
                                      value={rule.valueLabels.join(", ")}
                                      onChange={(e) =>
                                        updateSectionRule(selectedSectionIndex, rule.ruleId, {
                                          valueLabels: e.target.value
                                            .split(",")
                                            .map((v) => v.trim())
                                            .filter(Boolean),
                                        })
                                      }
                                      className="h-9 text-xs"
                                      placeholder="fiber, plan"
                                    />
                                  </Field>
                                  <div className="flex items-end">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeSectionRule(selectedSectionIndex, rule.ruleId)
                                      }
                                      className="inline-flex h-9 items-center justify-center rounded-md border border-border/40 bg-background px-3 text-muted-foreground transition-all hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>
                              </Surface>
                            ))
                          )}
                        </VerticalStack>
                      )}
                      <SuggestedPlacementsEditor
                        placements={selectedSection.suggestedPlacements}
                        productOptions={model.productOptions}
                        variantOptions={model.variantOptions}
                        onChange={(suggestedPlacements) =>
                          updateSection(selectedSectionIndex, { suggestedPlacements })
                        }
                      />
                    </div>
                  </VerticalStack>
                </div>
              )}
            </div>
          </div>
        );
      }

      case "metadata":
        return (
          <VerticalStack gap={8}>
            <section>
              <SectionHeading
                title="Advanced Metadata"
                subtitle="Attach secondary publication annotations for audit, integration, or downstream analytics context after the core publication behavior is settled."
              />
              
              <Surface className="mt-4 border-border/60 bg-muted/10 p-3 sm:mt-6 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-lg border border-border/40 bg-background p-2 text-foreground shadow-sm">
                    <Info className="h-4 w-4" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-black text-foreground">Operational Annotations</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Advanced metadata is secondary. Operators should be able to understand the publication without reading these entries.
                    </p>
                  </div>
                </div>
              </Surface>
            </section>

            <section>
              <div className="flex items-center justify-between gap-4 mb-4">
                <SectionHeading
                  title="Metadata Entries"
                  subtitle="Keep keys stable and values readable for downstream consumers."
                />
                <button
                  type="button"
                  onClick={addMetadataEntry}
                  className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-xs font-black text-primary-foreground transition-all hover:opacity-90 shadow-sm"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add metadata
                </button>
              </div>

              {model.value.metadata.length > 0 ? (
                <VerticalStack gap={4}>
                  {model.value.metadata.map((entry) => (
                    <Surface key={entry.id} className="border-border/60 p-4 sm:p-6">
                      <div className="grid gap-4 sm:gap-6 xl:grid-cols-[1.2fr_0.8fr_1fr_auto]">
                        <Field label="Key">
                          <Input
                            value={entry.key}
                            onChange={(event) => updateMetadataEntry(entry.id, { key: event.target.value })}
                            placeholder="channel_code"
                            disabled={entry.locked}
                            className="h-10 text-sm font-mono"
                          />
                        </Field>
                        <Field label="Type">
                          <Select
                            value={entry.valueType}
                            onValueChange={(valueType) =>
                              updateMetadataEntry(entry.id, {
                                valueType: valueType as CommerceMetadataValueType,
                                value: valueType === "boolean" ? "false" : updateValueForType(entry.value, valueType as CommerceMetadataValueType),
                              })
                            }
                            disabled={entry.locked}
                          >
                            <SelectTrigger className="h-10 text-sm"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {metadataValueTypeOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Value" description={entry.helperText ?? undefined}>
                          {entry.valueType === "boolean" ? (
                            <Select
                              value={entry.value || "false"}
                              onValueChange={(value) => updateMetadataEntry(entry.id, { value })}
                              disabled={entry.locked}
                            >
                              <SelectTrigger className="h-10 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">True</SelectItem>
                                <SelectItem value="false">False</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              value={entry.value}
                              onChange={(event) => updateMetadataEntry(entry.id, { value: event.target.value })}
                              placeholder={entry.valueType === "json" ? '{"tier":"gold"}' : entry.valueType === "number" ? "10" : "value"}
                              disabled={entry.locked}
                              className="h-10 text-sm font-mono"
                            />
                          )}
                        </Field>
                        <div className="flex items-end">
                          <button
                            type="button"
                            onClick={() => removeMetadataEntry(entry.id)}
                            disabled={entry.locked}
                            aria-label="Remove metadata entry"
                            className="inline-flex h-10 items-center justify-center rounded-md border border-border/40 bg-background px-4 text-muted-foreground transition-all hover:bg-red-50 hover:text-red-600 hover:border-red-200 disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </Surface>
                  ))}
                </VerticalStack>
              ) : (
                <Surface className="border-border/40 border-dashed bg-muted/5 p-12 text-center">
                  <DescriptionText>No advanced metadata yet.</DescriptionText>
                </Surface>
              )}
              <div className="mt-4">
                <FieldError message={model.validationErrors.metadata} />
              </div>
            </section>
          </VerticalStack>
        );

      default:
        return null;
    }
  };

  const isSectionsFillTab = resolvedActiveTabId === "sections";

  return (
    <WizardWorkspace
      className="absolute inset-0 z-50"
      isOpen={model.isOpen}
      isLoading={false}
      isSaving={model.isSaving || (resolvedActiveTabId === "access" ? !accessCanSave && model.isSaving : false)}
      title={model.title}
      subtitle={model.subtitle}
      tabs={tabs}
      activeTabId={resolvedActiveTabId}
      onTabChange={setActiveTabId}
      saveLabel={resolvedActiveTabId === "access" && accessCanSave ? "Save Access Rules" : model.saveActionLabel}
      onSave={handlePrimaryAction}
      onClose={model.onClose}
      discardLabel="Close"
      fillContent={isSectionsFillTab}
      summaryEyebrow="Publication Summary"
      summaryItems={[
        { label: "Status", value: getOptionLabel(model.statusOptions, model.value.status, "Draft"), complete: model.value.status === "active" || model.value.status === "published" },
        { label: "Storefront", value: model.value.storefrontKey || "None", complete: !!model.value.storefrontKey },
        { label: "Catalog", value: getOptionLabel(model.catalogOptions, model.value.catalogId, "None"), complete: !!model.value.catalogId },
        { label: "Scope", value: scopePresentation.label, complete: true },
        { label: "Selection", value: selectionPresentation.label, complete: true },
        { label: "Sections", value: model.value.sections.length > 0 ? `${model.value.sections.length} defined` : "None", complete: true },
      ]}
    >
      {isSectionsFillTab ? (
        renderTabContent()
      ) : (
        <div className="max-w-4xl">
          {model.error && (
            <div className="mb-6 rounded-xl border border-destructive/20 bg-destructive/5 p-4 text-destructive">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="text-sm font-semibold">{model.error}</p>
              </div>
            </div>
          )}

          {model.callouts.map((callout) => (
            <div key={callout.id} className={cn("mb-6 rounded-xl border p-4 text-sm", getToneClasses(callout.tone))}>
              <div className="flex items-start gap-3">
                {callout.tone === "warning" || callout.tone === "danger"
                  ? <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  : <Info className="mt-0.5 h-4 w-4 shrink-0" />}
                <div>
                  <p className="font-semibold">{callout.title}</p>
                  <p className="mt-1 text-xs opacity-90">{callout.body}</p>
                </div>
              </div>
            </div>
          ))}

          {renderTabContent()}
        </div>
      )}
    </WizardWorkspace>
  );
}
